import { AddressFormFieldNames } from 'shared/enums/addressFormFieldNames';
import { Field, FieldProps, useFormikContext } from 'formik';
import Autocomplete from 'react-google-autocomplete';
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NsKey } from 'enums/NsKey';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { FormField } from '../FormField';
import { clsx } from 'clsx';

// Moved to environment variables or constants file
const GOOGLE_AUTOCOMPLETE_API_KEY = process.env.REACT_APP_GOOGLE_AUTOCOMPLETE_API_KEY;

// Improved type definition with better naming
interface AddressFormValues {
  [AddressFormFieldNames.Address]: string;
  [AddressFormFieldNames.PostalCode]: string;
  [AddressFormFieldNames.City]: string;
  [AddressFormFieldNames.Country]: string;
  [AddressFormFieldNames.GooglePlaceId]: string;
  [AddressFormFieldNames.Latitude]: number;
  [AddressFormFieldNames.Longitude]: number;
  [key: string]: any;
}

// Enhanced props with better documentation
interface GoogleLocationProps {
  name: string;
  label?: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** ISO country code to restrict the search (e.g., 'fr' for France) */
  countryRestriction?: string;
  /** Time in milliseconds to wait before processing changes */
  debounceTime?: number;
  /** Optional callback when place is selected */
  onPlaceSelected?: (place: google.maps.places.PlaceResult) => void;
  /** Optional prefix for nested address fields (e.g., 'futureAddress' for 'futureAddress.line1') */
  fieldPrefix?: string;
}

export const FormikGoogleLocation: React.FC<GoogleLocationProps> = ({
  name,
  label,
  required,
  placeholder,
  disabled = false,
  className = '',
  countryRestriction = 'fr',
  debounceTime = 300, // Increased default for better UX
  onPlaceSelected,
  fieldPrefix
}) => {
  const [currentPlace, setCurrentPlace] = useState<google.maps.places.PlaceResult | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useDynamicTranslation([NsKey.Borrower]);

  const { values, setFieldValue, setFieldTouched, setFieldError, validateForm } = useFormikContext<AddressFormValues>();
  const prevPlaceId = useRef<string | null>(null);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Garde-fou : le backfill du place_id sur une adresse pré-remplie (édition) ne doit
  // se faire qu'une seule fois par instance.
  const placeIdBackfillDone = useRef(false);

  // Resolve nested field value from dot-notation path (e.g. "renegotiation.entityAddressSearch")
  const getNestedValue = useCallback((obj: any, path: string): any => {
    return path.split('.').reduce((acc, key) => acc?.[key], obj);
  }, []);

  // Helper to get field name with optional prefix
  const getFieldName = useCallback(
    (field: string): string => {
      return fieldPrefix ? `${fieldPrefix}.${field}` : field;
    },
    [fieldPrefix]
  );

  // Vue multi-champs : l'input piloté par l'autocomplete EST le champ "adresse" lui-même
  // (CP/Ville/Pays sont des champs séparés) -> il ne doit contenir QUE la rue.
  // Vue mono-champ (barre de recherche dédiée, fieldPrefix défini) -> on garde l'adresse
  // complète formatée, les sous-champs étant cachés.
  const isStreetOnlyField = useMemo(
    () => name === getFieldName(AddressFormFieldNames.Address),
    [name, getFieldName]
  );

  // Improved component extraction with memoization
  const getComponent = useCallback(
    (type: string, components?: google.maps.GeocoderAddressComponent[]): string => {
      if (!components || !components.length) return '';

      const component = components.find(c => c.types.includes(type));
      return component?.long_name || '';
    },
    []
  );

  // Improved error handling and address field updates
  const updateAddressFields = useCallback(async () => {
    // Guard clauses for better readability
    if (!currentPlace || !currentPlace.formatted_address) {
      return;
    }

    // Skip if place hasn't changed
    if (prevPlaceId.current === currentPlace.place_id) {
      return;
    }

    // Update reference and trigger callback if provided
    prevPlaceId.current = currentPlace.place_id || null;
    onPlaceSelected?.(currentPlace);

    const { address_components, place_id, formatted_address } = currentPlace;

    // Extract address parts more reliably
    const addressParts = formatted_address.split(',');
    const address = addressParts[0]?.trim() || '';

    const postalCode = getComponent('postal_code', address_components);
    const city = getComponent('locality', address_components) ||
      getComponent('administrative_area_level_2', address_components);
    const country = address_components?.find(c => c.types.includes('country'))?.short_name || '';

    setLoading(true);
    setError(null);

    try {
      // Les coordonnées viennent de la sélection elle-même (geometry demandé par
      // l'Autocomplete) : aucun aller-retour réseau n'est nécessaire pour remplir les
      // champs. Le géocodage ne sert que de repli si geometry manque.
      let lat = currentPlace.geometry?.location?.lat() ?? null;
      let lng = currentPlace.geometry?.location?.lng() ?? null;

      if (lat == null || lng == null) {
        try {
          const results = await geocodeByAddress(formatted_address);
          if (results.length) {
            const coords = await getLatLng(results[0]);
            lat = coords.lat;
            lng = coords.lng;
          }
        } catch (geocodeError) {
          // Coordonnées indisponibles : on remplit quand même l'adresse.
          console.warn('Geocoding fallback failed:', geocodeError);
        }
      }

      // Update form values in batch to reduce re-renders
      const fieldsToUpdate = {
        [getFieldName(AddressFormFieldNames.Address)]: address,
        [getFieldName(AddressFormFieldNames.PostalCode)]: postalCode,
        [getFieldName(AddressFormFieldNames.City)]: city,
        [getFieldName(AddressFormFieldNames.Country)]: country,
        [getFieldName(AddressFormFieldNames.GooglePlaceId)]: place_id,
        [getFieldName(AddressFormFieldNames.Latitude)]: lat,
        [getFieldName(AddressFormFieldNames.Longitude)]: lng
      };

      Object.keys(fieldsToUpdate).forEach(field => {
        setFieldTouched(field, true, false);
      });

      Object.entries(fieldsToUpdate).forEach(([field, value]) => {
        setFieldValue(field, value, false);
      });

      Object.keys(fieldsToUpdate).forEach((field) => {
        setFieldError(field, undefined);
      });

      // Clear validation error on the search field now that a place is selected
      setFieldError(name, undefined);

      // Multi-champs : s'assurer que l'input n'affiche que la rue (l'Autocomplete a pu
      // y réinjecter l'adresse complète après la sélection).
      if (isStreetOnlyField && inputRef.current) {
        inputRef.current.value = address;
      }

      // Trigger full form revalidation so isValid updates
      setTimeout(() => validateForm(), 0);

    } catch (error) {
      console.error('Error updating address fields:', error);
      setError(error instanceof Error ? error.message : 'Error updating address fields');
    } finally {
      setLoading(false);
    }
  }, [currentPlace, getComponent, getFieldName, setFieldTouched, setFieldValue, onPlaceSelected, setFieldError, isStreetOnlyField, validateForm, name]);

  // Improved debounce handling
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (currentPlace) {
      debounceTimer.current = setTimeout(() => {
        updateAddressFields();
      }, debounceTime);
    }

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [currentPlace, updateAddressFields, debounceTime]);

  // Backfill du place_id en édition : le back ne renvoie pas le googlePlaceId, donc une
  // adresse pré-remplie n'a pas d'id -> le formulaire serait invalide alors que l'utilisateur
  // n'a rien modifié. On géocode l'adresse existante en arrière-plan pour récupérer un id.
  // Dès que l'utilisateur retape l'adresse, handleInputChange vide à nouveau le place_id.
  useEffect(() => {
    if (placeIdBackfillDone.current) return;

    const existingPlaceId = getNestedValue(values, getFieldName(AddressFormFieldNames.GooglePlaceId));
    if (existingPlaceId) {
      // Déjà un id (création après sélection, ou back qui le renvoie un jour) : rien à faire.
      prevPlaceId.current = existingPlaceId;
      placeIdBackfillDone.current = true;
      return;
    }

    const line1 = getNestedValue(values, getFieldName(AddressFormFieldNames.Address));
    const city = getNestedValue(values, getFieldName(AddressFormFieldNames.City));
    // On ne backfill que sur une adresse complète pré-remplie (édition). En création, la
    // ville n'est renseignée qu'APRÈS une sélection Google (qui pose déjà le place_id), donc
    // "ville présente + pas d'id" caractérise de façon fiable le cas édition. Cela évite de
    // géocoder un texte partiel pendant la saisie.
    if (!line1 || !city) return;

    const query = [
      line1,
      getNestedValue(values, getFieldName(AddressFormFieldNames.PostalCode)),
      city,
      getNestedValue(values, getFieldName(AddressFormFieldNames.Country))
    ]
      .filter(Boolean)
      .join(', ');
    if (!query) return;

    placeIdBackfillDone.current = true;

    const waitForGoogle = async (retries = 20): Promise<boolean> => {
      for (let i = 0; i < retries; i++) {
        if ((window as any).google?.maps?.Geocoder) return true;
        await new Promise(resolve => setTimeout(resolve, 250));
      }
      return false;
    };

    (async () => {
      try {
        if (!(await waitForGoogle())) return;

        const results = await geocodeByAddress(query);
        const placeId = results[0]?.place_id;
        if (!placeId) return;

        const { lat, lng } = await getLatLng(results[0]);

        prevPlaceId.current = placeId;
        setFieldValue(getFieldName(AddressFormFieldNames.GooglePlaceId), placeId, false);
        setFieldValue(getFieldName(AddressFormFieldNames.Latitude), lat, false);
        setFieldValue(getFieldName(AddressFormFieldNames.Longitude), lng, false);

        // Relance la validation pour débloquer la soumission (place_id désormais présent).
        setTimeout(() => validateForm(), 0);
      } catch (error) {
        // Échec du géocodage : on laisse le place_id vide, l'utilisateur devra resélectionner.
        console.warn('Background place_id backfill failed:', error);
      }
    })();
  }, [values, getFieldName, getNestedValue, setFieldValue, validateForm]);

  // Pré-remplissage externe (ex. lookup SIRET) : l'input Google est non contrôlé
  // (defaultValue), donc une mise à jour Formik programmatique n'apparaît pas dans le
  // DOM. On synchronise l'input quand la valeur change hors saisie (input non focus).
  const formikAddressValue = getNestedValue(values, name);
  useEffect(() => {
    if (!inputRef.current || document.activeElement === inputRef.current) {
      return;
    }
    const display = formikAddressValue ?? '';
    if (inputRef.current.value !== display) {
      inputRef.current.value = display;
    }
  }, [formikAddressValue]);

  // Improved input change handler
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setFieldValue(name, newValue);

    // Reset all address fields if user manually edits (covers both Google selection and pre-filled data)
    const hasExistingAddress = prevPlaceId.current || getNestedValue(values, getFieldName(AddressFormFieldNames.City));
    if (hasExistingAddress && newValue !== getNestedValue(values, name)) {
      prevPlaceId.current = null;

      // Clear all address data to force a new Google place selection
      setFieldValue(getFieldName(AddressFormFieldNames.Address), '', false);
      setFieldValue(getFieldName(AddressFormFieldNames.PostalCode), '', false);
      setFieldValue(getFieldName(AddressFormFieldNames.City), '', false);
      setFieldValue(getFieldName(AddressFormFieldNames.Country), '', false);
      setFieldValue(getFieldName(AddressFormFieldNames.GooglePlaceId), '', false);
      setFieldValue(getFieldName(AddressFormFieldNames.Latitude), null, false);
      setFieldValue(getFieldName(AddressFormFieldNames.Longitude), null, false);
    }
  }, [name, getFieldName, getNestedValue, setFieldValue, values]);

  // Handle blur to trigger validation
  const handleBlur = useCallback(() => {
    setFieldTouched(name, true);
  }, [name, setFieldTouched]);

  // Handle place selection
  const handlePlaceSelected = useCallback((place: google.maps.places.PlaceResult) => {
    if (place && place.formatted_address) {
      setCurrentPlace(place);
      // Multi-champs : ne garder que la rue dans l'input adresse (CP/Ville/Pays sont ailleurs).
      // Mono-champ : conserver l'adresse complète formatée renvoyée par Google.
      const display = isStreetOnlyField
        ? place.formatted_address.split(',')[0].trim()
        : place.formatted_address;
      setFieldValue(name, display);
      // L'Autocomplete Google force la valeur complète dans l'input : on la corrige côté DOM.
      if (inputRef.current) {
        inputRef.current.value = display;
      }
    }
  }, [name, setFieldValue, isStreetOnlyField]);

  return (
    <Field name={name}>
      {({ meta }: FieldProps) => (
        <FormField
          label={label}
          required={required}
          name={name}
          error={meta.touched && meta.error ? t(meta.error, { defaultValue: meta.error }) : error || ''}
        >
          <div className={clsx('relative', className)}>
            <Autocomplete
              ref={inputRef}
              id={`google-location-${name}`}
              className={clsx(
                "w-full px-4 py-4 rounded border border-gray-300 text-sm font-normal text-gray-700 box-border",
                "placeholder:text-gray-500 placeholder:italic",
                "focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary",
                "h-12"
              )}
              apiKey={GOOGLE_AUTOCOMPLETE_API_KEY}
              placeholder={placeholder}
              onChange={handleInputChange}
              onBlur={handleBlur}
              onPlaceSelected={handlePlaceSelected}
              defaultValue={getNestedValue(values, name)}
              disabled={disabled}
              options={{
                types: ['geocode', 'establishment'],
                componentRestrictions: { country: countryRestriction }
              }}
            />
            {loading && (
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
              </div>
            )}
          </div>
        </FormField>
      )}
    </Field>
  );
};
